import React, { useState, useCallback, useEffect } from 'react';
import './ClientTable.css';

const ClientTable = ({ 
  readOnly = false,
  clients, 
  loading, 
  error, 
  updateClient, 
  revertClient,
  handleSort, 
  getSortIndicator, 
  isLowStock, 
  formatPrice,
  tableLocked,
  updating,
  clearEditingState = false,
  fieldLabels = {
    clientId: 'ID Cliente',
    name: 'Nome',
    email: 'Email',
    company: 'Empresa',
    phone: 'Telefone',
    address: 'Endereço',
    city: 'Cidade',
    state: 'Estado',
    zipCode: 'CEP',
    actions: 'Ações'
  }
}) => {

  const [editingCells, setEditingCells] = useState(new Set()); // Track multiple editing cells
  const [editValues, setEditValues] = useState({}); // Track edit values for each cell
  const [originalValues, setOriginalValues] = useState({}); // Track original values for each cell
  // const [isInitializing, setIsInitializing] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelCellInfo, setCancelCellInfo] = useState(null); // {clientId, field}

  // Clear editing state when clearEditingState prop changes
  useEffect(() => {
    if (clearEditingState) {
      setEditingCells(new Set());
      setEditValues({});
      setOriginalValues({});
    }
  }, [clearEditingState]);

  // Check if a cell has changes
  const hasCellChanges = useCallback((clientId, field) => {
    const cellKey = `${clientId}-${field}`;
    const editValue = editValues[cellKey];
    const originalValue = originalValues[cellKey];
    
    if (editValue === undefined || originalValue === undefined) {
      return false;
    }
    
    // Convert both values to strings for comparison
    const editStr = String(editValue).trim();
    const originalStr = String(originalValue).trim();
    
    const hasChanges = editStr !== originalStr;
    
    return hasChanges;
  }, [editValues, originalValues]);

  // Check if any cell in a row has changes
  const hasRowChanges = useCallback((clientId) => {
    const client = clients.find(c => c.clientId === clientId);
    if (!client) return false;
    
    const editableFields = ['name', 'email', 'company', 'phone', 'address', 'city', 'state', 'zipCode'];
    return editableFields.some(field => hasCellChanges(clientId, field));
  }, [clients, hasCellChanges]);

  // Start editing a cell
  const startEditing = useCallback((clientId, field) => {
    if (readOnly || updating || tableLocked) return;
    
    const client = clients.find(c => c.clientId === clientId);
    if (!client) return;

    const cellKey = `${clientId}-${field}`;
    const currentValue = client[field] || '';
    
    setEditingCells(prev => new Set(prev).add(cellKey));
    setEditValues(prev => ({
      ...prev,
      [cellKey]: currentValue
    }));
    setOriginalValues(prev => ({
      ...prev,
      [cellKey]: currentValue
    }));
  }, [readOnly, updating, tableLocked, clients]);

  // Update edit value
  const updateEditValue = useCallback((clientId, field, value) => {
    const cellKey = `${clientId}-${field}`;
    setEditValues(prev => ({
      ...prev,
      [cellKey]: value
    }));
  }, []);

  // Save a cell
  const saveCell = useCallback(async (clientId, field) => {
    const cellKey = `${clientId}-${field}`;
    const newValue = editValues[cellKey];
    const originalValue = originalValues[cellKey];
    
    // If no changes, just exit editing mode
    if (String(newValue).trim() === String(originalValue).trim()) {
      setEditingCells(prev => {
        const newSet = new Set(prev);
        newSet.delete(cellKey);
        return newSet;
      });
      return;
    }

    try {
      const success = await updateClient(clientId, field, newValue);
      if (success) {
        setEditingCells(prev => {
          const newSet = new Set(prev);
          newSet.delete(cellKey);
          return newSet;
        });
      }
    } catch (error) {
      console.error('Error saving cell:', error);
    }
  }, [editValues, originalValues, updateClient]);

  // Cancel editing a cell
  const cancelEditing = useCallback((clientId, field) => {
    const cellKey = `${clientId}-${field}`;
    const hasChanges = hasCellChanges(clientId, field);
    
    if (hasChanges) {
      setCancelCellInfo({ clientId, field });
      setShowCancelModal(true);
    } else {
      setEditingCells(prev => {
        const newSet = new Set(prev);
        newSet.delete(cellKey);
        return newSet;
      });
    }
  }, [hasCellChanges]);

  // Confirm cancel
  const confirmCancel = useCallback(() => {
    if (cancelCellInfo) {
      const { clientId, field } = cancelCellInfo;
      const cellKey = `${clientId}-${field}`;
      
      // Revert the client field to its original value
      const originalValue = originalValues[cellKey];
      if (originalValue !== undefined) {
        revertClient(clientId, field, originalValue);
      }
      
      setEditingCells(prev => {
        const newSet = new Set(prev);
        newSet.delete(cellKey);
        return newSet;
      });
      
      setEditValues(prev => {
        const newValues = { ...prev };
        delete newValues[cellKey];
        return newValues;
      });
    }
    
    setShowCancelModal(false);
    setCancelCellInfo(null);
  }, [cancelCellInfo, originalValues, revertClient]);

  // Cancel the cancel operation
  const cancelCancel = useCallback(() => {
    setShowCancelModal(false);
    setCancelCellInfo(null);
  }, []);

  // Revert entire row
  const revertRow = useCallback(async (clientId) => {
    try {
      await revertClient(clientId);
    } catch (error) {
      console.error('Error reverting row:', error);
    }
  }, [revertClient]);

  // Check if a cell is being edited
  const isEditing = useCallback((clientId, field) => {
    const cellKey = `${clientId}-${field}`;
    return editingCells.has(cellKey);
  }, [editingCells]);

  // Get current value for a cell (edit value or original)
  const getCurrentValue = useCallback((clientId, field) => {
    const cellKey = `${clientId}-${field}`;
    return editValues[cellKey] !== undefined ? editValues[cellKey] : '';
  }, [editValues]);

  // Handle key press in input
  const handleKeyPress = useCallback((e, clientId, field) => {
    if (e.key === 'Enter') {
      saveCell(clientId, field);
    } else if (e.key === 'Escape') {
      cancelEditing(clientId, field);
    }
  }, [saveCell, cancelEditing]);

  // Render cell content
  const renderCell = useCallback((client, field) => {
    const isEditingCell = isEditing(client.clientId, field);
    const hasChanges = hasCellChanges(client.clientId, field);
    const currentValue = getCurrentValue(client.clientId, field);

    if (isEditingCell && !readOnly) {
      return (
        <input
          type="text"
          value={currentValue}
          onChange={(e) => updateEditValue(client.clientId, field, e.target.value)}
          onBlur={() => saveCell(client.clientId, field)}
          onKeyDown={(e) => handleKeyPress(e, client.clientId, field)}
          className={`client-input ${hasChanges ? 'has-changes' : ''}`}
          autoFocus
        />
      );
    }

    return (
      <span 
        className={`client-cell-value ${hasChanges ? 'has-changes' : ''}`}
        onClick={() => !readOnly && startEditing(client.clientId, field)}
        title={hasChanges ? 'Clique para editar (alterações pendentes)' : 'Clique para editar'}
      >
        {client[field] || '-'}
      </span>
    );
  }, [isEditing, hasCellChanges, getCurrentValue, readOnly, updateEditValue, saveCell, handleKeyPress, startEditing]);

  if (loading) {
    return (
      <div className="client-table-loading">
        <div className="spinner"></div>
        <p>Carregando clientes...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="client-table-error">
        <p>❌ Erro ao carregar clientes: {error}</p>
        <button onClick={() => window.location.reload()} className="retry-button">
          Tentar Novamente
        </button>
      </div>
    );
  }

  if (!clients || clients.length === 0) {
    return (
      <div className="client-table-empty">
        <p>Nenhum cliente encontrado.</p>
      </div>
    );
  }

  return (
    <>
      <div className="client-table-container">
        <div className="client-table-header">
          <h3>Clientes ({clients.length})</h3>
          {updating && (
            <div className="updating-indicator">
              <div className="spinner small"></div>
              <span>Salvando alterações...</span>
            </div>
          )}
        </div>

        <div className="client-table-wrapper">
          <table className="client-table">
            <thead>
              <tr>
                <th 
                  className="sortable"
                  onClick={() => handleSort('clientId')}
                >
                  {fieldLabels.clientId} {getSortIndicator('clientId')}
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort('name')}
                >
                  {fieldLabels.name} {getSortIndicator('name')}
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort('email')}
                >
                  {fieldLabels.email} {getSortIndicator('email')}
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort('company')}
                >
                  {fieldLabels.company} {getSortIndicator('company')}
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort('phone')}
                >
                  {fieldLabels.phone} {getSortIndicator('phone')}
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort('city')}
                >
                  {fieldLabels.city} {getSortIndicator('city')}
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort('state')}
                >
                  {fieldLabels.state} {getSortIndicator('state')}
                </th>
                {!readOnly && <th>{fieldLabels.actions}</th>}
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <tr 
                  key={client.clientId} 
                  className={`client-row ${isLowStock(client) ? 'low-stock' : ''} ${hasRowChanges(client.clientId) ? 'has-changes' : ''}`}
                >
                  <td className="client-id-cell">
                    <span className="client-id">{client.clientId}</span>
                  </td>
                  <td className="editable-cell">
                    {renderCell(client, 'name')}
                  </td>
                  <td className="editable-cell">
                    {renderCell(client, 'email')}
                  </td>
                  <td className="editable-cell">
                    {renderCell(client, 'company')}
                  </td>
                  <td className="editable-cell">
                    {renderCell(client, 'phone')}
                  </td>
                  <td className="editable-cell">
                    {renderCell(client, 'city')}
                  </td>
                  <td className="editable-cell">
                    {renderCell(client, 'state')}
                  </td>
                  {!readOnly && (
                    <td className="actions-cell">
                      {hasRowChanges(client.clientId) && (
                        <button
                          onClick={() => revertRow(client.clientId)}
                          className="revert-button"
                          title="Reverter alterações"
                          disabled={updating}
                        >
                          ↶
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Descartar Alterações?</h3>
            <p>Você tem alterações não salvas. Deseja descartá-las?</p>
            <div className="modal-actions">
              <button onClick={confirmCancel} className="confirm-button">
                Descartar
              </button>
              <button onClick={cancelCancel} className="cancel-button">
                Continuar Editando
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClientTable;
